import { Eye, EyeOff } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { AnimatedDots } from '../../components/ui/AnimatedDots';
import { AuthPageLayout } from '../../components/layout/AuthPageLayout';
import { supabase } from '../../lib/supabase';
import { getAuthErrorMessage } from '../../utils/authErrorMessages';
import { decodeJWTPayload } from '../../utils/jwt';
import { hasIncompleteOnboarding, getOnboardingUrl } from '../../utils/onboardingCheck';

/**
 * Fixed notices keyed by the page that sent the user here. InviteCodeSignup
 * sends `confirm-email` when Supabase withheld the session pending email
 * confirmation. Keyed rather than free text so a crafted link cannot put its
 * own words on the login form — and because the free-text `?message=` it used
 * to send was never read: the user landed on a bare form, signed in with the
 * right password, and was told "Invalid email or password" (2026-10-06).
 */
const SIGNIN_NOTICES: Record<string, string> = {
  'confirm-email':
    'Your account was created. Open the confirmation link we emailed you, then log in here.',
};

// Toast hook similar to mobile app
function useToast(duration = 2500) {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = React.useCallback((message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setToast({ message, type });
    timeoutRef.current = setTimeout(() => setToast(null), duration);
  }, [duration]);

  return { toast, showToast };
}

export default function Signin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  // QR-flow context: when a user lands on /signin from a QR scan or self-enroll
  // failure, these params let us auto-join them to the gym after login.
  const qrCompanyId = searchParams.get('companyId');
  const qrLocationId = searchParams.get('locationId');
  const qrPrefillEmail = searchParams.get('email');
  const notice = SIGNIN_NOTICES[searchParams.get('notice') ?? ''] ?? null;
  const [email, setEmail] = useState(qrPrefillEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast, showToast } = useToast();

  // If the Signin page was opened with a QR context (companyId in URL),
  // ensure the user has a user_company_relationship with that company and
  // switch their last_selected_company to it. Then route them into the app.
  const joinGymFromQRAndRoute = useCallback(async (userId: string) => {
    if (!qrCompanyId) return false;
    try {
      const { data: existingRelationship } = await supabase
        .from('user_company_relationships')
        .select('id, primary_location_id')
        .eq('user_id', userId)
        .eq('company_id', qrCompanyId)
        .maybeSingle();

      if (!existingRelationship) {
        const { data: studentRole } = await supabase
          .from('system_roles')
          .select('system_role_id')
          .eq('role', 'student')
          .single();
        if (studentRole?.system_role_id) {
          await supabase.from('user_company_relationships').insert({
            user_id: userId,
            company_id: qrCompanyId,
            role_id: studentRole.system_role_id,
            onboarding_complete: true,
            primary_location_id: qrLocationId || null,
          });
        }
      } else if (qrLocationId && existingRelationship.primary_location_id !== qrLocationId) {
        await supabase
          .from('user_company_relationships')
          .update({ primary_location_id: qrLocationId, updated_at: new Date().toISOString() })
          .eq('id', existingRelationship.id);
      }

      await supabase
        .from('user_profiles')
        .update({ last_selected_company: qrCompanyId })
        .eq('id', userId);
      await supabase.auth.refreshSession();
      navigate('/feed');
      return true;
    } catch {
      return false;
    }
  }, [qrCompanyId, qrLocationId, navigate]);

  // Helper to add timeout to async operations
  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
      ),
    ]);
  };

  // Helper to execute Supabase query with timeout
  // Supabase builders are thenable, so we can use Promise.resolve() to convert them
  const executeQueryWithTimeout = useCallback(async <T,>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryBuilder: any,
    timeoutMs: number,
    errorMessage: string
  ): Promise<T> => {
    const promise = Promise.resolve(queryBuilder) as Promise<T>;
    return withTimeout(promise, timeoutMs, errorMessage);
  }, []);

  // Helper to determine where to navigate after login
  // Uses JWT claims first (fast, no DB query) to avoid timeouts
  const getRedirectPath = useCallback(async (userId: string, isInviteFlow = false): Promise<string> => {
    // If a redirect parameter was provided, use it
    if (redirectTo) {
      return redirectTo;
    }

    // FIRST: Check for incomplete onboarding state in localStorage
    // This ensures users can continue onboarding across browser windows
    if (hasIncompleteOnboarding()) {
      const onboardingUrl = getOnboardingUrl();
      return onboardingUrl;
    }

    // FIRST: Check JWT claims for role (fast, no DB query needed)
    // This avoids timeout issues and gives us immediate role info
    try {
      const sessionPromise = supabase.auth.getSession();
      const { data: { session } } = await withTimeout(
        sessionPromise,
        5000,
        'Session check timeout'
      );
      
      if (session?.access_token) {
        const claims = decodeJWTPayload(session.access_token);
        const jwtRole = claims?.user_role || claims?.role;
        const isAdmin = claims?.is_admin === true;
        
        // If admin, route to admin dashboard immediately
        if (isAdmin) {
          return '/admin/dashboard';
        }
        
        // Check if user is admin or developer - redirect to admin dashboard
        if (claims?.is_admin === true || claims?.is_developer === true) {
          return '/admin/dashboard';
        }
        
        // If student role in JWT, check if they ALSO have owner/admin roles
        // before routing to the mobile-only page. Multi-role users (e.g., owner
        // of gym A + student at gym B) should NOT be sent to the student gate.
        if (jwtRole === 'student') {
          // Quick check: does this user also have owner/admin relationships?
          try {
            const { data: allRelationships } = await supabase
              .from('user_company_relationships')
              .select('role_id, system_roles(role)')
              .eq('user_id', userId);

            const allRoles = allRelationships
              ?.map((r: { system_roles: { role: string } | null }) => r.system_roles?.role)
              .filter((r): r is string => Boolean(r)) || [];
            const hasOwnerOrAdmin = allRoles.some((r) => ['owner', 'admin'].includes(r));

            if (hasOwnerOrAdmin) {
              // User has owner/admin roles — skip student routing entirely,
              // fall through to the owner check or database-based routing below.
            } else {
              // Pure student — check if they need onboarding
              const { data: { user } } = await supabase.auth.getUser();
              if (user) {
                const { data: profile } = await supabase
                  .from('user_profiles')
                  .select('profile_complete, eula_accepted_at')
                  .eq('id', user.id)
                  .single();

                const needsEula = !profile?.eula_accepted_at;
                const needsOnboarding = !profile?.profile_complete || needsEula;

                if (needsOnboarding) {
                  return '/student-onboarding';
                }
              }

              // Onboarding complete, route to student landing page
              return '/student-landing';
            }
          } catch {
            // Fall through to default routing
          }
        }

        // If owner role in JWT, check if company onboarding is incomplete
        if (jwtRole === 'owner') {
          try {
            // Get owner role ID
            const { data: ownerRole } = await supabase
              .from('system_roles')
              .select('system_role_id')
              .eq('role', 'owner')
              .single();

            if (ownerRole) {
              // Check if user has a company as owner with incomplete setup
              const { data: relationships } = await supabase
                .from('user_company_relationships')
                .select('company_id, companies(id, setup_complete)')
                .eq('user_id', userId)
                .eq('role_id', ownerRole.system_role_id);

              if (relationships && relationships.length > 0) {
                const ownerRelation = relationships[0];
                // If company exists but setup is not complete, redirect to onboarding
                if (ownerRelation.companies && !ownerRelation.companies.setup_complete) {
                  return '/onboarding';
                }
              } else {
                // No relationships but user has owner role in JWT - they need to complete onboarding
                return '/onboarding';
              }
            }
          } catch {
            // Fall through to default routing
          }
        }
        
        // If no role in JWT but user signed up as owner, check if they need onboarding
        if (!jwtRole || jwtRole === null) {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const signupIntent = user.user_metadata?.signup_intent;
              const userType = user.user_metadata?.user_type;
              
              // If user signed up as owner but has no role in JWT (no company created yet), redirect to onboarding
              if (signupIntent === 'owner' || userType === 'owner') {
                // Quick check if they have any relationships
                const { data: relationships } = await supabase
                  .from('user_company_relationships')
                  .select('company_id')
                  .eq('user_id', userId)
                  .limit(1);
                
                if (!relationships || relationships.length === 0) {
                  return '/onboarding';
                }
              }
            }
          } catch {
            // Fall through to default routing
          }
        }
      }
    } catch {
      // JWT parsing failed or timeout - fall back to database query
    }

    // Try database queries with timeout protection
    try {
      // Check if user is a developer (with timeout)
      const devProfileResult = await executeQueryWithTimeout<{
        data: { id: string; is_active: boolean } | null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: any;
      }>(
        supabase
          .from('developer_profiles')
          .select('id, is_active')
          .eq('id', userId)
          .maybeSingle(),
        5000,
        'Developer check timeout'
      );
      const devProfile = devProfileResult.data;

      if (devProfile?.is_active) {
        return '/admin/dashboard';
      }

      // Check relationships (with timeout)
      const relationshipsResult = await executeQueryWithTimeout<{
        data: Array<{
          company_id: string;
          role_id: string;
          system_roles: { role: string } | null;
        }> | null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: any;
      }>(
        supabase
          .from('user_company_relationships')
          .select(`
            company_id,
            role_id,
            system_roles (
              role
            )
          `)
          .eq('user_id', userId),
        10000,
        'Relationships check timeout'
      );
      const relationships = relationshipsResult.data;

      // Check for incomplete company onboarding (user started but didn't finish)
      // This checks the database, not just localStorage, so it works even if localStorage is cleared
      
      // FIRST: Check if user has NO relationships but signed up as an owner
      // This handles the case where user started onboarding but never completed company creation
      if (!relationships || relationships.length === 0) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const signupIntent = user.user_metadata?.signup_intent;
            const userType = user.user_metadata?.user_type;
            
            // If user signed up as owner but has no company relationships, they need to complete onboarding
            if (signupIntent === 'owner' || userType === 'owner') {
              return '/onboarding';
            }
          }
        } catch {
          // Fall through to continue with other checks
        }
      }
      
      // SECOND: Check if user has relationships but company setup is incomplete
      if (relationships && relationships.length > 0) {
        // Get owner role ID
        const ownerRoleResult = await executeQueryWithTimeout<{
          data: { system_role_id: string } | null;
          error: Error | null;
        }>(
          supabase
            .from('system_roles')
            .select('system_role_id')
            .eq('role', 'owner')
            .maybeSingle(),
          5000,
          'Owner role check timeout'
        );
        const ownerRole = ownerRoleResult.data;

        if (ownerRole) {
          // Check if user has a company as owner with incomplete setup
          const ownerRelationships = relationships.filter(
            (r) => r.role_id === ownerRole.system_role_id
          );

          if (ownerRelationships.length > 0) {
            // Check if any of the owner companies have incomplete setup
            const companyIds = ownerRelationships.map((r) => r.company_id);
            const companiesResult = await executeQueryWithTimeout<{
              data: Array<{ id: string; setup_complete: boolean }> | null;
              error: Error | null;
            }>(
              supabase
                .from('companies')
                .select('id, setup_complete')
                .in('id', companyIds),
              5000,
              'Companies check timeout'
            );
            const companies = companiesResult.data;

            // If user has a company with setup_complete = false, redirect to onboarding
            const incompleteCompany = companies?.find((c) => !c.setup_complete);
            if (incompleteCompany) {
              return '/onboarding';
            }
          }
        }
      }

      // Check admin status (with timeout)
      const adminCheckResult = await executeQueryWithTimeout<{
        data: { user_id: string } | null;
        error: Error | null;
      }>(
        supabase
          .from('admins')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle(),
        5000,
        'Admin check timeout'
      );
      const adminCheck = adminCheckResult.data;

      const isSystemAdmin = !!adminCheck;
      const hasMultipleCompanies = (relationships?.length || 0) > 1;

      // If system admin, route to admin dashboard
      if (isSystemAdmin) {
        return '/admin/dashboard';
      }

      // Check roles from relationships
      const roles = relationships?.map((r) => r.system_roles?.role).filter((r): r is string => Boolean(r)) || [];
      const isStudent = roles.includes('student') && !roles.some((r) => ['owner', 'admin'].includes(r));

      // If student (and not also owner/admin), route to student landing page
      if (isStudent) {
        return '/student-landing';
      }

      if (hasMultipleCompanies) {
        // Get current role for selected company (with timeout)
        const profileResult = await executeQueryWithTimeout<{
          data: { last_selected_company: string | null } | null;
          error: Error | null;
        }>(
          supabase
            .from('user_profiles')
            .select('last_selected_company')
            .eq('id', userId)
            .maybeSingle(),
          5000,
          'Profile check timeout'
        );
        const profile = profileResult.data;
        const profileError = profileResult.error;

        // Handle error or missing profile gracefully
        if (profileError) {
          // Fall back to first relationship if profile fetch fails
          const selectedRelationship = relationships?.[0];
          const currentRole = selectedRelationship?.system_roles?.role;
          if (isSystemAdmin || (currentRole && !['owner', 'admin'].includes(currentRole))) {
            return '/company-selector';
          }
          return '/dashboard';
        }

        const selectedCompanyId = profile?.last_selected_company;
        const selectedRelationship = selectedCompanyId
          ? relationships?.find((r) => r.company_id === selectedCompanyId) || relationships?.[0]
          : relationships?.[0];

        const currentRole = selectedRelationship?.system_roles?.role;

        // If system admin OR current role is not owner/admin, show selector
        if (isSystemAdmin || (currentRole && !['owner', 'admin'].includes(currentRole))) {
          return '/company-selector';
        }
      }

      return '/dashboard';
    } catch {
      // If all queries timeout, default to dashboard (better than hanging)
      return '/dashboard';
    }
  }, [redirectTo, executeQueryWithTimeout]);

  // Track if we've already checked/processed to prevent infinite loops
  const hasCheckedRef = useRef(false);

  // Check if user is already authenticated or handle magic link tokens
  useEffect(() => {
    // Prevent multiple checks
    if (hasCheckedRef.current) return;
    
    const checkUser = async () => {
      hasCheckedRef.current = true;
      try {
        // Check for magic link tokens in URL hash
        const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
        const hashParams = new URLSearchParams(hash);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // If magic link tokens are present, set session and route accordingly
        if (accessToken && refreshToken && type === 'magiclink') {
          try {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              showToast('Invalid or expired magic link. Please try again.', 'error');
              return;
            }

            // Get user to check role in the invited company only
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
              showToast('Failed to authenticate. Please try again.', 'error');
              return;
            }

            // Check signup intent to see if this is a student invite
            const signupIntent = user.user_metadata?.signup_intent;
            const isStudentInvite = signupIntent === 'student_with_invite' || signupIntent === 'draft_student_invite';

            if (isStudentInvite) {
              // Get company_id and draft_student_id from user_metadata (the company they were invited to)
              // All invites are for students only, so we check role only in this specific company
              const companyId = user.user_metadata?.company_id;
              const draftStudentId = user.user_metadata?.draft_student_id;
              const locationId = user.user_metadata?.location_id;
              
              if (companyId) {
                // CRITICAL: Ensure user_company_relationship exists for this company
                // The database trigger only fires on user creation/confirmation, not when setting session for existing users
                const { data: existingRelationship } = await supabase
                  .from('user_company_relationships')
                  .select('id')
                  .eq('user_id', user.id)
                  .eq('company_id', companyId)
                  .maybeSingle();

                if (!existingRelationship) {
                  // Relationship doesn't exist - create it
                  // Get student role ID
                  const { data: studentRole } = await supabase
                    .from('system_roles')
                    .select('system_role_id')
                    .eq('role', 'student')
                    .single();

                  if (studentRole) {
                    // Create the relationship
                    const { error: createRelError } = await supabase
                      .from('user_company_relationships')
                      .insert({
                        user_id: user.id,
                        company_id: companyId,
                        role_id: studentRole.system_role_id,
                        onboarding_complete: false, // Draft invites need onboarding
                        primary_location_id: locationId || null,
                      });

                    if (createRelError) {
                      // Continue anyway - might already exist from concurrent request
                    }

                    // Update draft student record if we have draft_student_id
                    if (draftStudentId) {
                      const { error: updateDraftError } = await supabase
                        .from('staging_import_members')
                        .update({
                          user_id: user.id,
                          invite_accepted_at: new Date().toISOString(),
                          status: 'registered',
                          updated_at: new Date().toISOString(),
                        })
                        .eq('id', draftStudentId)
                        .neq('status', 'registered'); // Only update if not already registered

                      if (updateDraftError) {
                        // Non-fatal - continue
                      }
                    }
                  }

                  // Refresh session to get updated JWT with company_id claim
                  await supabase.auth.refreshSession();
                }

                // Check user's role ONLY in the invited company (not other companies)
                const { data: relationship } = await supabase
                  .from('user_company_relationships')
                  .select(`
                    company_id,
                    system_roles (
                      role
                    )
                  `)
                  .eq('user_id', user.id)
                  .eq('company_id', companyId)
                  .maybeSingle();

                // Check if user needs onboarding before routing
                const { data: profile } = await supabase
                  .from('user_profiles')
                  .select('profile_complete, eula_accepted_at')
                  .eq('id', user.id)
                  .single();

                const needsEula = !profile?.eula_accepted_at;
                const needsOnboarding = !profile?.profile_complete || needsEula;

                if (needsOnboarding) {
                  navigate('/student-onboarding');
                  return;
                }

                // Check role ONLY in the invited company
                // All invites are for students, so they should always route to success page
                const role = relationship?.system_roles?.role;
                
                if (role === 'owner' || role === 'admin') {
                  // This shouldn't happen for invites, but if it does, route to dashboard
                  const path = await getRedirectPath(user.id);
                  navigate(path);
                } else {
                  // Student/coach/reception or no role yet - route to success page (all invites are students)
                  navigate('/draft-invite-success');
                }
              } else {
                // No company_id found - default to success page (all invites are students)
                navigate('/draft-invite-success');
              }
            } else {
              // Regular login (not a student invite) - route to dashboard
              // Pass false to indicate this is NOT an invite flow
              const path = await getRedirectPath(user.id, false);
              navigate(path);
            }

            // Clear hash to prevent re-processing
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            return;
          } catch {
            showToast('Failed to process magic link. Please try logging in manually.', 'error');
            return;
          }
        }

        // Check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // If this tab came from a QR scan (companyId in URL), auto-join and go to feed.
          if (await joinGymFromQRAndRoute(session.user.id)) return;

          // Check if this is an invite flow by looking at URL params or JWT metadata
          const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
          const hashParams = new URLSearchParams(hash);
          const isInviteFlow = hashParams.get('type') === 'invite' ||
                               hashParams.get('type') === 'signup' ||
                               session.user?.user_metadata?.signup_intent !== undefined;

          const path = await getRedirectPath(session.user.id, isInviteFlow);
          navigate(path);
        }
      } catch {
        hasCheckedRef.current = false; // Reset on error to allow retry
      }
    };
    checkUser();
  }, [navigate, redirectTo, showToast, getRedirectPath]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      showToast('Email and password are required.', 'error');
      return;
    }

    setLoading(true);

    try {
      // Add timeout to login request (30 seconds max)
      const loginPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      const { data, error: signInError } = await withTimeout(
        loginPromise,
        30000,
        'Login request timed out. Please check your connection and try again.'
      );

      if (signInError) {
        // Show user-friendly error message
        const friendlyMessage = getAuthErrorMessage(signInError);
        showToast(friendlyMessage, 'error');
        setLoading(false);
        return;
      }
      
      if (!data.user) {
        showToast('Authentication failed. Please try again.', 'error');
        setLoading(false);
        return;
      }

      // Log user session for analytics (non-blocking)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          // Get IP address and user agent
          const ipAddress = null; // Could be extracted from request headers if available
          const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
          
          // Log session (non-blocking - don't wait for it)
          // Note: Using type assertion because RPC function may not be in TypeScript types yet
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any).rpc('log_user_session', {
            p_user_id: data.user.id,
            p_session_token: session.access_token,
            p_ip_address: ipAddress,
            p_user_agent: userAgent,
            p_platform: 'web',
            p_metadata: {},
          }).then(() => {
            // Session logged successfully
          }).catch(() => {
            // Non-fatal - continue with login
          });
        }
      } catch {
        // Non-fatal - continue with login
      }

      // Don't show success toast yet - wait until redirect is determined
      // showToast('Login successful!', 'success');

      // If they arrived via a QR link (companyId in URL), auto-join the gym and
      // route them to the feed instead of the normal post-login destination.
      if (await joinGymFromQRAndRoute(data.user.id)) return;

      // Determine where to redirect (with timeout protection)
      // This is a normal login (not an invite flow)
      try {
        const path = await getRedirectPath(data.user.id, false);
        navigate(path);
      } catch {
        // Default to dashboard if redirect path determination fails
        showToast('Login successful! Redirecting...', 'success');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout showBackButton backTo="/" backLabel="Landing page">
      <Helmet>
        <title>Log In - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>

      <div className="w-full max-w-md flex flex-col items-center">
        {/* Logo and Brand */}
        <Link
          to="/"
          className="flex items-center space-x-4 mb-12 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            className="w-24 h-24 sm:w-32 sm:h-32"
          />
          <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </Link>

        {/* Login Card */}
        <Card className="w-full max-w-sm sm:max-w-md border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-xl sm:text-2xl font-bold text-center text-[#EBFFEE]">
              Log In
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notice && (
              <p
                role="status"
                className="mb-4 rounded-md border border-[#504A4A] bg-[#504A4A]/40 px-3 py-2 text-sm text-[#EBFFEE]"
              >
                {notice}
              </p>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Input */}
              <div className="space-y-2">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-md text-sm sm:text-base bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                  autoCapitalize="none"
                  autoComplete="email"
                  required
                  maxLength={254}
                />
              </div>

              {/* Password Input Container */}
              <div className="space-y-2">
                <div className="flex items-center rounded-md bg-[#504A4A] pr-2 transition-colors hover:bg-[#5A5454] focus-within:ring-2 focus-within:ring-[#9B1421]/50">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-md text-sm sm:text-base bg-transparent text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 text-[#EBFFEE] hover:text-[#A9A3A3] transition-colors cursor-pointer"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link
                  to="/reset-password"
                  className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors underline underline-offset-2"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                variant="red"
                size="lg"
                disabled={loading || !email || !password}
                className="w-full font-semibold"
              >
                {loading ? (
                  <AnimatedDots text="Logging in" className="text-white" />
                ) : (
                  'Log In'
                )}
              </Button>

              {/* Create Account Link */}
              <div className="flex justify-center pt-2">
                <span className="text-sm text-[#A9A3A3]">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="/signup"
                    className="text-rollops-accent hover:text-[#EBFFEE] font-medium transition-colors"
                  >
                    Create Account
                  </Link>
                </span>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className="fixed bottom-8 left-0 right-0 flex items-center justify-center px-6 z-50 pointer-events-none"
        >
          <div
            className={`px-6 py-4 rounded-lg text-center font-semibold max-w-md shadow-lg ${
              toast.type === 'error' 
                ? 'bg-[#9B1421] text-white' 
                : 'bg-green-600 text-white'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </AuthPageLayout>
  );
}
